// src/components/layout/ChatColumn/ActionButtons.jsx
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import { 
  Copy, Check, Rewind, Trash2, XCircle, History, BookmarkPlus, Wand2,
  LoaderPinwheel
} from 'lucide-react';
import { useConversation } from '@/managers/ConvoManager';
import { workspaceManager, DEFAULT_WORKSPACE } from '@/managers/workspaceManager';
import { isDefaultWorkspaceContextId, normalizeWorkspaceContextId } from '@/utils/workspaceContext';
import { HousePortal } from '@/App';
import DeleteMessage from './actions/DeleteMessage';
import HistoryRestart from './actions/HistoryRestart';
import ChatRewind from './actions/ChatRewind';

/* ========================
 * ActionButton - Memoized button component for individual actions
 * Receives stable onClick handlers for optimal performance.
 * Now accepts an iconClassName for custom icon styling.
 * ======================== */
const ActionButton = React.memo(({ 
  icon: Icon, onClick, tooltip, isActive = false, className = '', disabled = false, iconClassName = ''
}) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`p-1.5 rounded-md transition-all duration-200 hover:bg-surface-dark group relative ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
    aria-label={tooltip}
  >
    <Icon size={14} className={`transition-colors duration-200 ${isActive ? 'text-success' : 'text-on-surface opacity-60'} group-hover:text-on-surface group-hover:opacity-100 ${iconClassName}`} />
    {/* MODIFIED: Added explicit text-on-surface-dim for theme-aware tooltip text */}
    <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs bg-surface-dark text-on-surface-dim rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-50">
      {tooltip}
    </span>
  </button>
));
ActionButton.displayName = 'ActionButton';

/* ========================
 * DeleteModal - Memoized modal for delete confirmation
 * ======================== */
const DeleteModal = React.memo(({ show, messageId, onClose }) => {
  if (!show) return null;
  return (<HousePortal><DeleteMessage show={show} onClose={onClose} messageId={messageId} /></HousePortal>);
});
DeleteModal.displayName = 'DeleteModal';

/* ========================
 * RewindModal - Memoized modal for rewind confirmation
 * ======================== */
const RewindModal = React.memo(({ show, messageId, onClose }) => {
    const [errorToast, setErrorToast] = useState(null);
    const handleRewindError = (error) => { setErrorToast(error); setTimeout(() => setErrorToast(null), 5000); };
    if (!show) return null;
    return (
        <HousePortal>
            {errorToast && (<div className="fixed top-4 right-4 z- animate-slideIn"><div className="bg-error/90 text-white px-4 py-2 rounded-md shadow-lg">{errorToast}</div></div>)}
            <ChatRewind show={show} onClose={onClose} messageId={messageId} onError={handleRewindError} />
        </HousePortal>
    );
});
RewindModal.displayName = 'RewindModal';

/* ========================
 * RestartModal - Memoized modal for history restart confirmation
 * ======================== */
const RestartModal = React.memo(({ show, messageId, onClose }) => {
  if (!show) return null;
  return (<HousePortal><HistoryRestart show={show} onClose={onClose} messageId={messageId} /></HousePortal>);
});
RestartModal.displayName = 'RestartModal';

/* ========================
 * filterInternalCOT - Utility to remove internal Chain of Thought markers
 * ======================== */
const filterInternalCOT = (text) => {
  if (!text) return text;
  return text.replace(/[\s\S]*?<\/CDE_INTERNAL_COT>/g, '').trim();
};

/* ========================
 * ActionButtons - Component for message actions
 * Uses rawContent from the message object for copy operations.
 * Click handlers are memoized for performance.
 * ======================== */
const ActionButtons = React.memo(({
  message: fullMessageObject, // This is the full message object
  messageId,
  showActions, // This prop determines if the buttons are interactable (opacity controlled by parent)
  isUser = false,
  actions = [], // Custom actions array. Note: if actions contain functions, they also need to be stable.
  onCancel,     // Added for cancel button in BotBubble. Stability depends on parent.
  isStreaming   // Added for cancel button logic
}) => {
  const [modalStates, setModalStates] = useState({ delete: false, restart: false, rewind: false });
  const [copied, setCopied] = useState(false);
  const { currentConversation } = useConversation();
  const [isHoveringCancel, setIsHoveringCancel] = useState(false);
  const [workspaceState, setWorkspaceState] = useState(() => workspaceManager.getState());

  useEffect(() => {
    return workspaceManager.subscribe(setWorkspaceState);
  }, []);

  const contentForCopy = useMemo(() => {
    const raw = fullMessageObject?.rawContent;
    const regularContent = fullMessageObject?.content;
    let textToProcess = '';

    if (typeof raw === 'string') {
        textToProcess = raw;
    } else if (typeof regularContent === 'string') {
        textToProcess = regularContent;
    } else if (typeof regularContent === 'object' && regularContent !== null && typeof regularContent.content === 'string') {
        textToProcess = regularContent.content;
    } else if (Array.isArray(regularContent)) {
        textToProcess = regularContent.filter(c => c.type === 'text').map(c => c.text).join('\n');
    }
    
    return filterInternalCOT(textToProcess);
  }, [fullMessageObject]);

  const workspaceLabel = useMemo(() => {
    const workspaceId = normalizeWorkspaceContextId(currentConversation?.workspaceContextId);

    if (isDefaultWorkspaceContextId(workspaceId)) {
      return DEFAULT_WORKSPACE.workspaceName || 'Main Workspace';
    }

    const matchingWorkspace = (workspaceState.workspaces || []).find((workspace) => workspace.id === workspaceId);
    return matchingWorkspace?.workspaceName || workspaceId;
  }, [currentConversation?.workspaceContextId, workspaceState.workspaces]);

  const toggleModal = useCallback((modalType) => {
    setModalStates(prev => ({ ...prev, [modalType]: !prev[modalType] }));
  }, []);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(contentForCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  }, [contentForCopy]);

  // Memoized click handlers for ActionButton instances
  const handleDeleteClick = useCallback(() => toggleModal('delete'), [toggleModal]);
  const handleRewindClick = useCallback(() => toggleModal('rewind'), [toggleModal]);
  const handleRestartClick = useCallback(() => toggleModal('restart'), [toggleModal]);
  
  const handleSaveSnippetClick = useCallback(() => {
    window.dispatchEvent(new CustomEvent('open-add-snippet-modal', { 
      detail: { code: contentForCopy, language: '' }
    }));
  }, [contentForCopy]);

  return (
    <>
      <motion.div
        className="flex items-end gap-1"
      >
        {isStreaming && typeof onCancel === 'function' && !isUser ? (
            <div
              onMouseEnter={() => setIsHoveringCancel(true)}
              onMouseLeave={() => setIsHoveringCancel(false)}
              className="flex items-center"
            >
                <ActionButton
                    icon={isHoveringCancel ? XCircle : LoaderPinwheel}
                    onClick={onCancel}
                    tooltip={isHoveringCancel ? "Cancel response" : "Streaming..."}
                    iconClassName={`${isHoveringCancel ? 'text-error' : 'text-on-surface-dim'} ${!isHoveringCancel ? 'animate-spin' : ''}`}
                />
            </div>
        ) : (
          <>
            <ActionButton icon={Trash2} onClick={handleDeleteClick} tooltip="Delete message" />
            {isUser && (<ActionButton icon={Rewind} onClick={handleRewindClick} tooltip="Rewind to here" />)}
            <ActionButton icon={History} onClick={handleRestartClick} tooltip="Restart chat history from here" />
            <ActionButton
                icon={BookmarkPlus}
                onClick={handleSaveSnippetClick}
                tooltip="Save snippet"
            />
            <ActionButton 
                icon={copied ? Check : Copy} 
                onClick={handleCopy} 
                tooltip={copied ? "Copied!" : "Copy message"} 
                isActive={copied} 
                disabled={!contentForCopy} 
            />
          </>
        )}
        <div className="select-none self-end min-w-0 max-w-[112px] text-on-surface-dim leading-[1.05]">
          <span className="block text-[6px] font-medium uppercase tracking-[0.16em] opacity-70">
            Workspace
          </span>
          <span className="block truncate text-[8px] font-medium normal-case tracking-normal" title={workspaceLabel}>
            {workspaceLabel}
          </span>
        </div>
      </motion.div>

      <DeleteModal show={modalStates.delete} messageId={messageId} onClose={handleDeleteClick} />
      {isUser && <RewindModal show={modalStates.rewind} messageId={messageId} onClose={handleRewindClick} />}
      <RestartModal show={modalStates.restart} messageId={messageId} onClose={handleRestartClick} />
    </>
  );
});
ActionButtons.displayName = 'ActionButtons';

ActionButton.propTypes = {
  icon: PropTypes.elementType.isRequired,
  onClick: PropTypes.func.isRequired,
  tooltip: PropTypes.string.isRequired,
  isActive: PropTypes.bool,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  iconClassName: PropTypes.string // Added prop type for the new property
};

ActionButtons.propTypes = {
  message: PropTypes.object,
  messageId: PropTypes.string.isRequired,
  showActions: PropTypes.bool.isRequired,
  isUser: PropTypes.bool,
  actions: PropTypes.array,
  onCancel: PropTypes.func,
  isStreaming: PropTypes.bool
};

export default ActionButtons;